<template>
  <v-card
    class="map-card"
    elevation="1"
  >
    <!-- Header -->
    <div class="map-header">
      <div class="map-title-block">
        <div class="map-title-icon">
          <v-icon color="primary" size="22">
            mdi-map-marker-outline
          </v-icon>
        </div>

        <div>
          <div class="map-title">
            Projektstandort
          </div>

          <div class="map-subtitle">
            Geografische Lage des Projekts
          </div>
        </div>
      </div>

      <v-chip
        v-if="hasValidCoordinates"
        color="primary"
        variant="tonal"
        size="small"
        class="coordinate-chip"
      >
        <v-icon start size="17">
          mdi-crosshairs-gps
        </v-icon>

        {{ formattedCoordinates }}
      </v-chip>
    </div>

    <v-divider />

    <!-- Map -->
    <div
      v-if="hasValidCoordinates"
      class="map-container"
    >
      <div
        ref="mapElement"
        class="map-canvas"
        :class="{
          'marker-grabbable': draggable && hoveringMarker && !dragging,
          'marker-grabbing': dragging,
        }"
        aria-label="Karte mit dem Projektstandort"
        :aria-describedby="resolvedAddress ? 'map-resolved-address' : undefined"
        role="application"
      ></div>

      <!-- Das Mausrad zoomt nur mit Modifikatortaste; ohne Hinweis wirkt das wie ein Defekt. -->
      <div
        v-if="showZoomHint"
        class="map-zoom-hint"
        role="status"
      >
        Zum Zoomen {{ zoomModifierLabel }} + Mausrad verwenden
      </div>
    </div>

    <!--
      Der Marker dient dem Korrigieren einer falsch aufgelösten Adresse. Ohne
      Hinweis probiert das niemand aus.
    -->
    <div v-if="draggable" class="map-drag-hint">
      <v-icon size="16" class="me-1">mdi-cursor-move</v-icon>
      Marker ziehen, um den Standort zu korrigieren
    </div>

    <!--
      Die vom Geokodierungsdienst gelieferte Adresse kann von der eingegebenen
      abweichen — genau das soll die Karte sichtbar machen.
    -->
    <div
      v-if="resolvedAddress"
      id="map-resolved-address"
      class="map-resolved-address"
    >
      <v-icon size="16" class="me-1">
        mdi-map-search-outline
      </v-icon>

      Gefunden: {{ resolvedAddress }}
    </div>

    <!-- Missing coordinates -->
    <div
      v-else
      class="map-empty-state"
      role="status"
    >
      <div class="map-empty-icon">
        <v-icon color="primary" size="38">
          mdi-map-marker-off-outline
        </v-icon>
      </div>

      <div class="text-subtitle-1 font-weight-bold">
        Kein Projektstandort verfügbar
      </div>

      <div class="text-body-2 text-medium-emphasis mt-1">
        Bitte geben Sie gültige Koordinaten in den Projektdetails an.
      </div>
    </div>
  </v-card>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue'

import 'ol/ol.css'

import Map from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import OSM from 'ol/source/OSM'
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import Style from 'ol/style/Style'
import CircleStyle from 'ol/style/Circle'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import { useGeographic } from 'ol/proj'
import Collection from 'ol/Collection'
import { defaults as defaultControls } from 'ol/control'
import { defaults as defaultInteractions, DragPan, MouseWheelZoom, Translate } from 'ol/interaction'
import { platformModifierKeyOnly } from 'ol/events/condition'

const props = defineProps({
  mapLat: {
    type: [Number, String],
    default: null,
  },

  mapLon: {
    type: [Number, String],
    default: null,
  },

  resolvedAddress: {
    type: String,
    default: '',
  },

  /* Marker verschiebbar machen, um eine falsche Geokodierung zu korrigieren. */
  draggable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:position'])

/*
 * OpenLayers bindet das Zoom-Rad plattformabhängig an ⌘ bzw. Strg
 * (platformModifierKeyOnly), der Hinweistext muss dem folgen.
 */
const zoomModifierLabel = computed(() => {
  const platform = navigator.platform || navigator.userAgent || ''

  return /Mac|iPod|iPhone|iPad/.test(platform) ? '⌘' : 'Strg'
})

const mapElement = ref(null)
const showZoomHint = ref(false)
const hoveringMarker = ref(false)
const dragging = ref(false)

let mainMap = null
let markerFeature = null
let resizeObserver = null
let zoomHintTimer = null

/*
 * OpenLayers will use longitude/latitude coordinates
 * directly after this function is called.
 */
useGeographic()

const latitude = computed(() => {
  return Number(props.mapLat)
})

const longitude = computed(() => {
  return Number(props.mapLon)
})

const hasValidCoordinates = computed(() => {
  return (
    Number.isFinite(latitude.value) &&
    Number.isFinite(longitude.value) &&
    latitude.value >= -90 &&
    latitude.value <= 90 &&
    longitude.value >= -180 &&
    longitude.value <= 180
  )
})

const coordinates = computed(() => {
  return [
    longitude.value,
    latitude.value,
  ]
})

const formattedCoordinates = computed(() => {
  if (!hasValidCoordinates.value) return ''

  return `${latitude.value.toFixed(5)}, ${longitude.value.toFixed(5)}`
})

/*
 * The marker uses OpenLayers drawing primitives instead
 * of an external marker image. This avoids mixed-content
 * errors and remains available without Google assets.
 */
const markerStyle = new Style({
  image: new CircleStyle({
    radius: 10,

    fill: new Fill({
      color: '#334f78',
    }),

    stroke: new Stroke({
      color: '#ffffff',
      width: 3,
    }),
  }),
})

/*
 * Ein Rad ohne Modifikatortaste zoomt nicht mehr. Ohne Rückmeldung wirkt die
 * Karte dabei kaputt, deshalb der kurz eingeblendete Hinweis.
 */
function handleWheel(event) {
  if (event.ctrlKey || event.metaKey) return

  showZoomHint.value = true

  clearTimeout(zoomHintTimer)

  zoomHintTimer = setTimeout(() => {
    showZoomHint.value = false
  }, 1400)
}

async function initializeMap() {
  if (
    !hasValidCoordinates.value ||
    !mapElement.value
  ) {
    return
  }

  await nextTick()

  markerFeature = new Feature({
    geometry: new Point(coordinates.value),
  })

  const markerSource = new VectorSource({
    features: [markerFeature],
  })

  const markerLayer = new VectorLayer({
    source: markerSource,
    style: markerStyle,
  })

  mainMap = new Map({
    target: mapElement.value,

    layers: [
      new TileLayer({
        source: new OSM(),
      }),

      markerLayer,
    ],

    view: new View({
      center: coordinates.value,
      zoom: 14,
    }),

    /*
     * Die Zoom-Schaltflächen sind nötig, weil das Mausrad unten an eine
     * Modifikatortaste gebunden wird — sonst gäbe es keine Möglichkeit mehr
     * zu zoomen. Die Attribution ist eine Lizenzbedingung der OSM-Kacheln
     * und wird deshalb dauerhaft (nicht einklappbar) angezeigt.
     */
    controls: defaultControls({
      rotate: false,
      attributionOptions: {
        collapsible: false,
      },
    }),

    interactions: defaultInteractions({
      dragPan: false,
      mouseWheelZoom: false,
    }).extend([
      new DragPan({
        condition: function (mapBrowserEvent) {
          /*
           * Mit der Maus wie gewohnt ziehen. Auf Touch-Geräten erst mit zwei
           * Fingern, damit eine Wischgeste die Seite scrollt und nicht in der
           * Karte hängen bleibt.
           */
          if (mapBrowserEvent.originalEvent?.pointerType === 'mouse') {
            return true
          }

          return this.getPointerCount() === 2
        },
      }),

      /*
       * Ohne Modifikatortaste zoomt das Rad nicht, sondern die Seite scrollt
       * weiter. Die Karte sitzt in einem langen Formular; ein Rad, das den
       * Scroll abfängt, ist dort eine Falle.
       */
      new MouseWheelZoom({
        condition: platformModifierKeyOnly,
      }),
    ]),
  })

  mapElement.value.addEventListener('wheel', handleWheel, { passive: true })

  if (props.draggable) {
    /*
     * Den Marker verschiebbar machen. Der Zweck ist das Korrigieren einer
     * falsch aufgelösten Adresse, nicht das feine Positionieren: PVGIS
     * interpoliert über ein Raster von Kilometern.
     */
    const translate = new Translate({
      features: new Collection([markerFeature]),
    })

    translate.on('translatestart', () => {
      dragging.value = true
    })

    translate.on('translateend', (event) => {
      dragging.value = false

      const [longitude, latitude] = event.features.item(0).getGeometry().getCoordinates()
      emit('update:position', { latitude, longitude })
    })

    mainMap.addInteraction(translate)

    /* Ohne Cursorwechsel ist nicht erkennbar, dass der Marker anfassbar ist. */
    mainMap.on('pointermove', (event) => {
      if (event.dragging) return
      hoveringMarker.value = mainMap.hasFeatureAtPixel(event.pixel)
    })
  }

  /*
   * React to card or viewport size changes.
   */
  resizeObserver = new ResizeObserver(() => {
    mainMap?.updateSize()
  })

  resizeObserver.observe(mapElement.value)

  requestAnimationFrame(() => {
    mainMap?.updateSize()
  })
}

function updateMapLocation() {
  if (
    !mainMap ||
    !markerFeature ||
    !hasValidCoordinates.value
  ) {
    return
  }

  const nextCoordinates = coordinates.value
  const current = markerFeature.getGeometry().getCoordinates()

  /*
   * Kam die Änderung vom Ziehen des Markers selbst, steht er bereits richtig.
   * Dann darf die Karte nicht nachzentrieren — sonst springt die Ansicht unter
   * der Hand des Nutzers weg.
   */
  const alreadyThere =
    Math.abs(current[0] - nextCoordinates[0]) < 1e-9 &&
    Math.abs(current[1] - nextCoordinates[1]) < 1e-9

  if (alreadyThere) return

  markerFeature.setGeometry(
    new Point(nextCoordinates)
  )

  mainMap.getView().animate({
    center: nextCoordinates,
    zoom: 14,
    duration: 350,
  })
}

function destroyMap() {
  resizeObserver?.disconnect()
  resizeObserver = null

  clearTimeout(zoomHintTimer)
  zoomHintTimer = null
  showZoomHint.value = false

  mapElement.value?.removeEventListener('wheel', handleWheel)

  if (mainMap) {
    mainMap.setTarget(undefined)
    mainMap = null
  }

  markerFeature = null
}

watch(
  hasValidCoordinates,
  async valid => {
    if (!valid) {
      destroyMap()
      return
    }

    if (!mainMap) {
      await nextTick()
      await initializeMap()
    }
  },
  {
    immediate: true,
    flush: 'post',
  }
)

watch(
  coordinates,
  () => {
    if (hasValidCoordinates.value) {
      updateMapLocation()
    }
  },
  {
    deep: true,
  }
)

onBeforeUnmount(() => {
  destroyMap()
})
</script>

<style scoped lang="scss">
.map-card {
  overflow: hidden;

  margin-top: 1.25rem;
  margin-bottom: 1.25rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 24px;

  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
}

/* ========================================
   Header
======================================== */

.map-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  min-height: 72px;
  padding: 0.85rem 1rem;

  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
}

.map-title-block {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.map-title-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 42px;
  height: 42px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.1);
}

.map-title {
  color: rgb(var(--v-theme-on-surface));

  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
}

.map-subtitle {
  margin-top: 0.1rem;

  color: rgba(var(--v-theme-on-surface), 0.62);

  font-size: 0.8rem;
  line-height: 1.35;
}

.coordinate-chip {
  flex-shrink: 0;

  font-variant-numeric: tabular-nums;
}

/* ========================================
   Map
======================================== */

.map-container {
  position: relative;

  width: 100%;
  height: 400px;

  overflow: hidden;

  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.map-canvas {
  width: 100%;
  height: 100%;
}

.map-zoom-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 3;

  transform: translate(-50%, -50%);

  padding: 0.6rem 1rem;

  border-radius: 999px;

  color: rgb(var(--v-theme-surface));
  background-color: rgba(var(--v-theme-on-surface), 0.78);

  font-size: 0.82rem;
  font-weight: 600;

  pointer-events: none;

  backdrop-filter: blur(4px);
}

.marker-grabbable {
  cursor: grab;
}

.marker-grabbing {
  cursor: grabbing;
}

.map-drag-hint {
  display: flex;
  align-items: center;

  padding: 0.5rem 1rem;

  color: rgba(var(--v-theme-on-surface), 0.62);

  font-size: 0.78rem;
}

.map-resolved-address {
  display: flex;
  align-items: center;

  padding: 0.6rem 1rem;

  color: rgba(var(--v-theme-on-surface), 0.72);
  background-color: rgba(var(--v-theme-primary), 0.03);

  font-size: 0.8rem;
  line-height: 1.35;
}

/* ========================================
   OpenLayers controls
======================================== */

.map-container :deep(.ol-control) {
  border-radius: 12px;
  background-color: rgba(var(--v-theme-surface), 0.88);
  backdrop-filter: blur(8px);
}

.map-container :deep(.ol-control button) {
  color: rgb(var(--v-theme-on-surface));
  background-color: transparent;
}

.map-container :deep(.ol-control button:hover) {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

/*
 * Die Attribution der OSM-Kacheln ist eine Lizenzbedingung und darf nicht
 * ausgeblendet werden — sie wird hier nur an das Kartendesign angepasst.
 */
.map-container :deep(.ol-attribution) {
  right: 0.5rem;
  bottom: 0.5rem;

  max-width: calc(100% - 1rem);

  border-radius: 999px;

  background-color: rgba(var(--v-theme-surface), 0.9);
}

.map-container :deep(.ol-attribution ul) {
  padding: 0.2rem 0.6rem;

  color: rgba(var(--v-theme-on-surface), 0.8);

  font-size: 0.7rem;
  text-shadow: none;
}

.map-container :deep(.ol-attribution a) {
  color: rgb(var(--v-theme-primary));
}

/* ========================================
   Empty state
======================================== */

.map-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  min-height: 300px;
  padding: 2rem 1rem;

  text-align: center;

  background-color: rgba(var(--v-theme-primary), 0.02);
}

.map-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 68px;
  height: 68px;
  margin-bottom: 1rem;

  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* ========================================
   Responsive
======================================== */

@media (max-width: 700px) {
  .map-card {
    border-radius: 20px;
  }

  .map-header {
    align-items: flex-start;
    padding: 0.75rem;
  }

  .map-title-icon {
    width: 38px;
    height: 38px;
  }

  .coordinate-chip {
    display: none;
  }

  .map-container {
    height: 340px;
  }
}
</style>